import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'
import {cn} from '@/lib/utils'

const headingVariants = cva('font-heading font-medium tracking-normal text-emphasis', {
    variants: {
        variant: {
            h1: 'text-3xl leading-11 lg:text-6xl',
            h2: 'text-2.5xl leading-11 lg:text-5xl',
            h3: 'text-xl leading-10 lg:text-4xl',
            h4: 'text-base leading-9 lg:text-3xl',
            h5: 'text-sm leading-9 lg:text-xl',
            h6: 'text-xs leading-9 lg:text-base'
        }
    },
    defaultVariants: {
        variant: 'h1'
    }
})

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'span' | 'p'
type HeadingVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

interface HeadingProps
    extends React.HTMLAttributes<HTMLElement>,
        VariantProps<typeof headingVariants> {
    tag?: HeadingTag
    variant?: HeadingVariant
    'data-testid': string | undefined
}

const isValidHeadingTag = (tag: string): tag is HeadingTag => {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'p'].includes(tag)
}

const isValidHeadingVariant = (variant: string): variant is HeadingVariant => {
    return ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(variant)
}

const Heading = React.forwardRef<HTMLElement, HeadingProps>(
    (
        {tag = 'h1', variant = 'h1', className, children, 'data-testid': dataTestId, ...props},
        ref
    ) => {
        // Default to 'h1' if `tag` is not valid
        const Tag = isValidHeadingTag(tag) ? tag : 'h1'
        const _variant = isValidHeadingVariant(variant) ? variant : 'h1'

        return (
            <Tag
                className={cn(headingVariants({variant: _variant, className}))}
                ref={ref as React.Ref<any>}
                {...props}
                data-testid={dataTestId || 'heading-Tag'}
            >
                {children}
            </Tag>
        )
    }
)

Heading.displayName = 'Heading'

export {Heading}
